<template>
  <div class="login-container">
    <!-- 顶部背景图 -->
    <div class="login-header">
      <img :src="headerImage" class="header-bg" />
      <h1 class="login-title">用户登录</h1>
    </div>

    <!-- 登录表单 -->
    <div class="login-form-wrapper">
      <div class="login-form">
        <van-field
          v-model="form.mobile"
          type="tel"
          label="手机号"
          placeholder="请输入手机号码"
          class="form-item"
          maxlength="11"
          @input="handleMobileInput"
        >
          <template #button>
            <van-button
              size="small"
              :type="sendBtnType"
              :disabled="!canSendCode"
              @click="handleSendCode"
              class="send-btn"
            >
              {{ sendBtnText }}
            </van-button>
          </template>
        </van-field>

        <van-field
          v-model="form.code"
          center
          clearable
          label="验证码"
          placeholder="请输入验证码"
          class="form-item"
          @click="handleClearCode"
        />

        <!-- 协议说明 -->
        <div class="agreement-section">
          <div class="agreement-text">
            <span>您即将要使用的功能需要进行身份认证，关于使用和隐私保护政策请查看</span>
            <a 
              href="https://abch5.lenovoimage.com/pages/agreement/index_weixin.html" 
              class="agreement-link"
              @click.prevent="showAgreement('service')"
            >
              《服务协议》
            </a>
            <span>和</span>
            <a 
              href="https://abch5.lenovoimage.com/pages/privacy/privacy_weixin.html" 
              class="agreement-link"
              @click.prevent="showAgreement('privacy')"
            >
              《隐私协议》
            </a>
            <span>了解更多内容。</span>
          </div>
          
          <van-checkbox 
            v-model="form.agreed" 
            icon-size="16px" 
            class="agreement-checkbox"
          >
            我知晓并同意以上隐私政策
          </van-checkbox>
        </div>

        <!-- 登录按钮 -->
        <van-button
          type="primary"
          round
          block
          color="#1989fa"
          :disabled="!canLogin"
          :loading="loading"
          @click="handleLogin"
          class="login-btn"
        >
          {{ loading ? '登录中...' : '完成登录' }}
        </van-button>
      </div>
    </div>

    <!-- 协议弹窗 -->
    <van-dialog
      v-model="showAgreementDialog"
      :title="agreementTitle"
      show-cancel-button
    >
      <div class="agreement-content">
        <p>请仔细阅读相关协议内容...</p>
      </div>
    </van-dialog>
  </div>
</template>

<script>
import Vue from 'vue'
import md5 from 'js-md5'
import { Dialog, Toast } from 'vant'

export default {
  name: 'LoginPage',
  data() {
    return {
      form: {
        mobile: '',
        code: '',
        agreed: false
      },
      loading: false,
      countdown: 0,
      countdownTimer: null,
      showAgreementDialog: false,
      agreementType: '',
      headerImage: require('@/assets/loginbk.png')
    }
  },
  
  computed: {
    isMobileValid() {
      return /^1[3-9]\d{9}$/.test(this.form.mobile)
    },
    
    canSendCode() {
      return this.isMobileValid && this.countdown === 0
    },
    
    canLogin() {
      return this.form.mobile && 
             this.form.code && 
             this.form.agreed && 
             !this.loading
    },
    
    sendBtnType() {
      return this.canSendCode ? 'primary' : 'default'
    },
    
    sendBtnText() {
      if (this.countdown > 0) {
        return this.countdown + 's后重新发送'
      }
      return '获取验证码'
    },
    
    agreementTitle() {
      return this.agreementType === 'service' ? '服务协议' : '隐私协议'
    }
  },
  
  mounted() {
    document.title = '用户登录'
  },
  
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
    }
  },
  
  methods: {
    handleMobileInput() {
      this.form.mobile = this.form.mobile.replace(/\D/g, '')
    },
    
    handleClearCode() {
      this.form.code = ''
    },
    
    handleSendCode() {
      if (!this.canSendCode) return
      
      this.loading = true
      this.sendVerificationCode()
        .then(() => {
          this.startCountdown()
          Toast.success('验证码发送成功')
        })
        .catch((error) => {
          console.error('发送验证码失败:', error)
          Toast.fail('验证码发送失败，请重试')
        })
        .finally(() => {
          this.loading = false
        })
    },
    
    sendVerificationCode() {
      const timestamp = new Date().getTime()
      const SECRET_KEY = '1234567890'
      const APP_ID = 'svchome'
      const params = SECRET_KEY + 'appid=' + APP_ID + 'mobile=' + this.form.mobile + 'timestamp=' + timestamp + SECRET_KEY
      const sign = md5(params)
      
      return Vue.axios.get(
        'https://t3.lenovoimage.com/api/login2/sendcode?appid=' + APP_ID + '&timestamp=' + timestamp + '&mobile=' + this.form.mobile + '&sign=' + sign
      )
    },
    
    startCountdown() {
      this.countdown = 60
      this.countdownTimer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
        }
      }, 1000)
    },
    
    handleLogin() {
      if (!this.canLogin) return
      
      this.loading = true
      this.userLogin()
        .then((response) => {
          if (response.data.Status === "0") {
            this.handleLoginSuccess(response.data)
          } else {
            this.handleLoginError(response.data.Message)
          }
        })
        .catch((error) => {
          this.handleLoginError(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    
    userLogin() {
      const timestamp = new Date().getTime()
      const SECRET_KEY = '1234567890'
      const APP_ID = 'svchome'
      const params = SECRET_KEY + 'appid=' + APP_ID + 'mobile=' + this.form.mobile + 'timestamp=' + timestamp + 'validcode=' + this.form.code + SECRET_KEY
      const sign = md5(params)
      
      return Vue.axios.get(
        'https://t3.lenovoimage.com/api/login2/codelogin?appid=' + APP_ID + '&timestamp=' + timestamp + '&mobile=' + this.form.mobile + '&validcode=' + this.form.code + '&sign=' + sign
      )
    },
    
    handleLoginSuccess(data) {
      localStorage.setItem('access_token', data.token || '')
      localStorage.setItem('userIcon', data.userIcon || '')
      localStorage.setItem('customername', data.customername || '')
      localStorage.setItem('isLogin', '1')
      
      Toast.success('登录成功')
      this.$router.push({ path: '/' })
    },
    
    handleLoginError(error) {
      console.error('登录失败:', error)
      let message = '登录失败，请重试'
      
      if (error.response && error.response.data && error.response.data.Message) {
        message = error.response.data.Message
      } else if (error.Message) {
        message = error.Message
      }
      
      Dialog.alert({
        title: '登录失败',
        message: message
      })
      
      this.clearLoginState()
    },
    
    clearLoginState() {
      localStorage.removeItem('access_token')
      localStorage.removeItem('userIcon')
      localStorage.removeItem('customername')
      localStorage.setItem('isLogin', '0')
    },
    
    showAgreement(type) {
      this.agreementType = type
      this.showAgreementDialog = true
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.login-header {
  position: relative;
  height: 30vh;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.3;
}

.login-title {
  position: relative;
  color: white;
  font-size: 24px;
  font-weight: 600;
}

.login-form-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  margin-top: -50px;
}

.login-form {
  background: white;
  width: 100%;
  max-width: 400px;
  padding: 30px 25px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.form-item {
  margin-bottom: 20px;
}

.send-btn {
  border-radius: 6px;
  font-size: 12px;
  min-width: 80px;
}

.agreement-section {
  margin: 30px 0;
}

.agreement-text {
  font-size: 12px;
  line-height: 1.6;
  color: #969799;
  text-align: left;
  margin-bottom: 15px;
}

.agreement-link {
  color: #1989fa;
  text-decoration: none;
}

.agreement-checkbox {
  font-size: 14px;
  color: #323233;
}

.login-btn {
  margin-top: 10px;
  height: 44px;
  font-size: 16px;
  font-weight: 600;
}

.login-btn:disabled {
  opacity: 0.6;
}

.agreement-content {
  padding: 20px;
  color: #666;
  line-height: 1.6;
}

@media (max-width: 375px) {
  .login-form-wrapper {
    padding: 15px;
  }
  
  .login-form {
    padding: 25px 20px;
  }
  
  .login-title {
    font-size: 20px;
  }
}

@media (min-height: 700px) {
  .login-form-wrapper {
    margin-top: -80px;
  }
}

@media (min-height: 800px) {
  .login-form-wrapper {
    margin-top: -100px;
  }
}
</style>